/**
 * Company-timezone helpers for every timesheet surface.
 *
 * `time_entries.clock_in` / `clock_out` are Postgres `timestamptz` and arrive
 * on the client as absolute UTC instants. Rendering them with bare
 * `format(new Date(iso), ...)` uses the *browser's* timezone, so a manager in
 * Germany sees a 09:00 Bali shift as 03:00.
 *
 * Every timesheet read (display, grouping, filtering, export) and every
 * timesheet write (datetime-local inputs) must go through these helpers so the
 * company's timezone — `company_settings.timezone` — is the only thing that
 * decides the wall clock shown.
 */
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { startOfMonth, addMonths, startOfWeek, endOfWeek } from 'date-fns';

export const FALLBACK_TZ = 'UTC';

/** Validate an IANA timezone name, falling back to UTC. */
export const safeTz = (tz?: string | null): string => {
  if (!tz || !tz.trim()) return FALLBACK_TZ;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return FALLBACK_TZ;
  }
};

/** Format a UTC instant with a date-fns pattern, in the company timezone. */
export const formatInCompanyTz = (
  value: string | Date,
  tz: string | null | undefined,
  pattern: string
): string => formatInTimeZone(value, safeTz(tz), pattern);

/** "MMM dd, yyyy" in the company timezone. */
export const formatEntryDate = (value: string | Date, tz?: string | null): string =>
  formatInCompanyTz(value, tz, 'MMM dd, yyyy');

/** "h:mm a" in the company timezone. */
export const formatEntryTime = (value: string | Date, tz?: string | null): string =>
  formatInCompanyTz(value, tz, 'h:mm a');

/** "yyyy-MM-dd" day bucket key in the company timezone. */
export const entryDayKey = (value: string | Date, tz?: string | null): string =>
  formatInCompanyTz(value, tz, 'yyyy-MM-dd');

/**
 * UTC instant -> value for an `<input type="datetime-local">`, expressed as the
 * company-timezone wall clock ("yyyy-MM-ddTHH:mm").
 */
export const toCompanyDatetimeLocal = (value: string | Date, tz?: string | null): string =>
  formatInCompanyTz(value, tz, "yyyy-MM-dd'T'HH:mm");

/**
 * Inverse of `toCompanyDatetimeLocal`: a datetime-local string is read as a
 * wall clock **in the company timezone** and converted back to a UTC instant.
 */
export const fromCompanyDatetimeLocal = (
  localValue: string,
  tz?: string | null
): Date => fromZonedTime(localValue, safeTz(tz));

/** Same as above, returned as an ISO-8601 UTC string. */
export const companyDatetimeLocalToISO = (
  localValue: string,
  tz?: string | null
): string => fromCompanyDatetimeLocal(localValue, tz).toISOString();

/** Current moment as a company-timezone datetime-local string (for input `max`). */
export const nowAsCompanyDatetimeLocal = (tz?: string | null): string =>
  toCompanyDatetimeLocal(new Date(), tz);

/**
 * Month boundaries [start, end) as UTC instants matching the company-timezone
 * calendar month of `month`. Keeps fetched rows aligned with rendered dates.
 */
export const companyMonthRange = (
  month: Date,
  tz?: string | null
): { startISO: string; endISO: string } => {
  const zone = safeTz(tz);
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const start = startOfMonth(month);
  const end = startOfMonth(addMonths(month, 1));
  return {
    startISO: fromZonedTime(`${ymd(start)}T00:00:00`, zone).toISOString(),
    endISO: fromZonedTime(`${ymd(end)}T00:00:00`, zone).toISOString(),
  };
};

/**
 * Week boundaries (Sunday-anchored, system convention) as UTC instants matching
 * the company-timezone calendar week containing `reference`.
 */
export const companyWeekRange = (
  reference: Date,
  tz?: string | null,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 0
): { startISO: string; endISO: string } => {
  const zone = safeTz(tz);
  // Anchor on the company-tz wall clock of `reference` so the week doesn't
  // slip when the browser is a day ahead/behind.
  const zoned = toZonedTime(reference, zone);
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const start = startOfWeek(zoned, { weekStartsOn });
  const end = endOfWeek(zoned, { weekStartsOn });
  return {
    startISO: fromZonedTime(`${ymd(start)}T00:00:00`, zone).toISOString(),
    endISO: fromZonedTime(`${ymd(end)}T23:59:59.999`, zone).toISOString(),
  };
};

/** UTC offset label for a timezone, e.g. "UTC+8". */
export const companyDayRangeBounds = (
  startYmd: string,
  endYmd: string,
  tz?: string | null
): { startISO: string; endExclusiveISO: string } => {
  const zone = safeTz(tz);
  // End boundary is exclusive: start of the day AFTER the end date, so the
  // whole final company-local day is included (mirrors the payroll Edge Function).
  const [y, m, d] = endYmd.split('-').map(Number);
  const nextDay = new Date(Date.UTC(y, m - 1, d + 1));
  const nextYmd = `${nextDay.getUTCFullYear()}-${String(nextDay.getUTCMonth() + 1).padStart(2, '0')}-${String(nextDay.getUTCDate()).padStart(2, '0')}`;
  return {
    startISO: fromZonedTime(`${startYmd}T00:00:00`, zone).toISOString(),
    endExclusiveISO: fromZonedTime(`${nextYmd}T00:00:00`, zone).toISOString(),
  };
};

/** UTC offset label for a timezone, e.g. "UTC+8". */
export const tzOffsetLabel = (tz?: string | null, at: Date = new Date()): string => {
  const zone = safeTz(tz);
  const raw = formatInTimeZone(at, zone, 'xxx'); // "+08:00" / "Z"
  if (raw === 'Z') return 'UTC';
  const [h, m] = raw.slice(1).split(':');
  const sign = raw[0];
  const hours = Number(h);
  return `UTC${sign}${hours}${m !== '00' ? `:${m}` : ''}`;
};

/** Hint copy, e.g. "Times shown in Asia/Makassar (UTC+8)". */
export const companyTimezoneHint = (tz?: string | null): string => {
  const zone = safeTz(tz);
  return `Times shown in ${zone} (${tzOffsetLabel(zone)})`;
};
